import { useQuery } from '@tanstack/react-query';
import { api } from '../api-client.config';

export interface TeamStat {
  statType: string;
  value: number;
}

export interface PlayerStat {
  playerId: string;
  playerName: string;
  stats: Record<string, number>;
}

export interface MatchStatsResponse {
  match: {
    id: string;
    leagueId: string;
    homeTeamId: string;
    awayTeamId: string;
    homeTeamName: string;
    awayTeamName: string;
    homeTeamAbbreviation: string;
    awayTeamAbbreviation: string;
    scheduledDate: string;
    clock: string;
    status: 'scheduled' | 'live' | 'finished' | 'postponed' | 'cancelled';
    homeScore?: number;
    awayScore?: number;
  };
  teamStats: {
    home: TeamStat[];
    away: TeamStat[];
  };
  playerStats: {
    home: PlayerStat[];
    away: PlayerStat[];
  };
}

export const getMatchStats = async (matchId: string): Promise<MatchStatsResponse> => {
  const response = (await api.get(`/feed/matches/${matchId}/statistics`)) as MatchStatsResponse;
  return response;
};

export const useGetFeedMatchStatsQuery = (matchId: string | undefined) => {
  return useQuery({
    queryKey: ['feed', 'matches', matchId, 'statistics'],
    queryFn: () => {
      if (!matchId) throw new Error('Match ID are required');
      return getMatchStats(matchId);
    },
    enabled: !!matchId,
    staleTime: 30 * 1000, // 30 seconds - stats can change during live matches
    refetchInterval: query => {
      const data = query.state.data as MatchStatsResponse | undefined;
      if (!data?.match) return false;
      const { match } = data;
      const isLive = match.status === 'live';
      // scheduledDate is ISO 8601 UTC (e.g. 2026-01-31T17:00:00.000Z) - compare timestamps for locale-independent check
      const scheduledTime = new Date(match.scheduledDate).getTime();
      const isPastScheduled = !isNaN(scheduledTime) && scheduledTime < Date.now();

      return isLive && isPastScheduled ? 60 * 1000 : false; // Refetch every minute for live matches
    },
  });
};
